import * as React from 'react';
import { useEffect } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { isDesktop } from '../utils/environment';
import { StatusBar } from './statusBar/StatusBar';
import { SaveToContainer } from './saveToContainer/SaveToContainer';
import { FoldersContainer } from './foldersContainer/FoldersContainer';
import officeApi from '../../api/officeApi';
import Progress from './shared/Progress';
import { StatusBarAlert } from './statusBar/StatusBarAlert';
import { DisposableAlert } from './shared/DisposableAlert';
import { clearAlertText } from '../actions/index';
import SaveViewSkeleton from './shared/Skeletons/SaveViewSkeleton';
import * as FileActions from '../actions/files';
import { handleAttachmentsByPlatform } from '../utils/attachments';
import { StatusBarTimeReminder } from './statusBar/StatusBarTimeReminder';
import { UninterruptedModeBanner } from './statusBar/UninterruptedModeBanner';
import TimeFormContainer from './timeFormContainer/TimeFormContainer';
import SalesforceRecordFormContainer from './salesforceRecordForm/SalesforceRecordFormContainer';

import './Email.scss';

let isInitalized = false;

const Email = () => {
  const dispatch = useDispatch();
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const isNavigatingFolders = useSelector((state) => state.folders.isNavigatingFolders);
  const isAddingTime = useSelector((state) => state.time.isAddingTime);
  const isSalesforceRecordFormOpen = useSelector((state) => state.salesforceRecords.isSalesforceRecordFormOpen);
  const isOfficeInitialized = useSelector((state) => state.app.isOfficeInitialized);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const initializationError = useSelector((state) => state.app.initializationError);
  const authFailure = useSelector((state) => state.auth.authenticationFailure);
  const requestingLogout = useSelector((state) => state.auth.requestingLogout);
  const isLoading = useSelector((state) => state.app.isLoading);
  const existingAttachments = useSelector((state) => state.files.docrioFiles);
  const { selectAttachmentsByDefault, minimumFileSize } = useSelector((state) => state.mail.settings?.docrioSettings?.attachments);
  const uninterruptedModeEnabled = useSelector((state) => state.mail.settings?.syncSettingsAndUsageReport?.uninterruptedModeEnabled);
  const hasRefreshTokenIssue = useSelector((state) => state.auth.user?.hasRefreshTokenIssue);
  const error = initializationError || authFailure;
  const selectedFiles = useSelector((state) => state.files.selectedFiles);
  const email = useSelector((state) => state.mail.emails);
  const draftId = useSelector((state) => state.mail.draftId);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  const docrioNamespace = useSelector((state) => state.mail.settings?.override?.docrioNamespace || 'litify_docs');
  const folderMaps = useSelector((state) => state.folders.folderMaps);
  const fileMaps = useSelector((state) => state.files.fileMaps);
  const alertText = useSelector((state) => state.app.alertText);
  const existingAttachmentId = new Set(existingAttachments.map((attachment) => attachment.id).filter(Boolean));
  const docrioFiles = new Map(Object.entries(selectedFiles).map(([docrioId, { name, folderPath }]) => [name, { docrioId, folderPath }]));
  const watchAttachments = useSelector((state) => state.files.attachedFiles);
  const showTimeReminder = useSelector((state) => state.time.showTimeReminder);
  const isRemovingSaveLocation = useSelector((state) => state.mail.isRemovingSaveLocation);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const defaultFolderSettings = useSelector((state) => state.mail.settings?.docrioSettings?.defaultFolderSettings || {});

  const getFolderMap = (docrioId) => {
    let relatedTo = null;
    let folderPathArray = null;
    outerLoop:
    for (const relatedToId in fileMaps) {
      if (Object.hasOwn(fileMaps, relatedToId)) {
        const folderMap = fileMaps[relatedToId];
        for (const folder in folderMap) {
          if (Object.hasOwn(folderMap, folder)) {
            for (const file of folderMap[folder]) {
              if (file.Id === docrioId) {
                relatedTo = file[`${docrioNamespace}__Related_To__c`];
                folderPathArray = JSON.parse(file[`${docrioNamespace}__Folder_Path__c`]);
                break outerLoop; // Break out of all loops
              }
            }
          }
        }
      }
    }
    if (!relatedTo || !folderPathArray) {
      return {};
    }
    return { [relatedTo]: folderMaps[relatedTo][folderPathArray.join('/')] };
  };

  const renderAttachments = async (attachments) => {
    // handle new attachments.
    const newAttachments = attachments.filter((attachment) => !existingAttachmentId.has(attachment.id));
    for (const newAttachment of newAttachments) {
      let docrioFile = docrioFiles.get(newAttachment.name);
      if (!docrioFile) {
        docrioFile = docrioFiles.get(newAttachment.name + '.eml') || docrioFiles.get(newAttachment.name + '.email') || null;
      }
      if (docrioFile) {
        await Object.assign(newAttachment, docrioFile);
        newAttachment.toSave = true;
      }
      const folderPaths = {};
      if (selectAttachmentsByDefault) {
        Object.keys(selectedRecords).forEach((recordId) => {
          const defaultAttachmentFolderPath = defaultFolderSettings[selectedRecords[recordId].type]?.saveAttachmentsFolderPath;
          if (!isComposingEmail || (isComposingEmail && !newAttachment.docrioId)) {
            folderPaths[recordId] = {
              path: defaultAttachmentFolderPath,
              name: JSON.parse(defaultAttachmentFolderPath || '[]')?.pop() || `${defaultFolderSettings[selectedRecords[recordId].type].objectLabel} Home`,
              subfolders: [],
            };
          }
        });
      }
      newAttachment.folderPaths = folderPaths;
      if (!newAttachment.docrioId && isComposingEmail) {
        newAttachment.toSave = selectAttachmentsByDefault;
      }
      if (!newAttachment.folderPaths || (newAttachment.folderPaths && JSON.stringify(newAttachment.folderPaths) === '{}')) {
        newAttachment.folderPaths = getFolderMap(newAttachment.docrioId);
      }
    }

    const attachmentHashes = new Set(attachments.map((attachment) => attachment.id));
    // Files unpacked from a zip are not Outlook attachments, so they are kept alongside the ones that are.
    const currentAttachments = existingAttachments.filter((attachment) => attachmentHashes.has(attachment.id) || attachment.extractedFrom);
    const docrioEml = existingAttachments.filter((existingAtt) => [email.itemId, draftId].includes(existingAtt.id));
    const files = [...docrioEml, ...currentAttachments, ...newAttachments];
    batch(() => {
      dispatch(FileActions.setDocrioFiles(files));
    });
  };

  const attachHandler = async () => {
    // Calling handleAttachmentsByPlatform on Desktop in read mode always leads to an error to we skip it
    if (isOfficeInitialized && !(isDesktop() && !isComposingEmail)) {
      try {
        const attachments = await handleAttachmentsByPlatform(email.itemId || draftId, minimumFileSize, ownerEmail, dispatch, !!draftId);
        await renderAttachments(attachments);
      } catch (error) {
        console.error(error);
      }
    }
    dispatch(FileActions.setDisableAddSaveLocation(false));
  };

  useEffect(() => {
    attachHandler();
  }, [watchAttachments]);

  if (!isInitalized && isComposingEmail) {
    officeApi.onAttachmentChanged(() => dispatch(FileActions.attachedFiles({})));
    isInitalized = true;
  }

  if (!isOfficeInitialized) {
    return (<Progress child={<SaveViewSkeleton />} />);
  }

  return (
    <div className='app-container'>
      <div className='main-container'>
        <div className='app-notifications'>
          {uninterruptedModeEnabled && hasRefreshTokenIssue && <UninterruptedModeBanner />}
          {alertText &&
            <DisposableAlert child={<StatusBarAlert />}
              disposeFunction={() => {
                !isAddingTime && !isRemovingSaveLocation && dispatch(clearAlertText());
              }}
              timeout={5000}
            />}
          {showTimeReminder && <StatusBarTimeReminder />}
        </div>
        {isLoading && <Progress />}
        {isNavigatingFolders ? <FoldersContainer /> : <>
          {isAddingTime ? <TimeFormContainer /> : <>
            {isAuthenticated && !error && !requestingLogout && !isSalesforceRecordFormOpen && <>
              <div id='status-bar'>
                <StatusBar />
              </div>
              <SaveToContainer />
            </>}
          </>}
        </>
        }
        {isSalesforceRecordFormOpen && <SalesforceRecordFormContainer />}
      </div>
    </div>
  );
};

export default Email;
